import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoveMoneyContext } from '../../context/MoveMoneyContext';
import '../MoveMoney/MoveMoney.css';
import { validateBeneficiary, initializeMoneyMovementJourney } from '../../api';

interface Beneficiary {
  receiverId: string;
  receiverFirstName: string;
  receiverLastName: string;
  receiverAccountNumber: string;
  receiverBank: string;
  receiverCurrency: string;
  receiverEmail: string;
  receiverCountry: string;
}

const Beneficiaries = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingBeneficiaries, setLoadingBeneficiaries] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const moveMoneyContext = useContext(MoveMoneyContext);

  if (!moveMoneyContext) {
    throw new Error('MoveMoneyContext must be used within a MoveMoneyProvider');
  }

  const {
    beneficiary,
    setBeneficiary,
    beneficiaries,
    setBeneficiaries,
    setAccounts,
    setEventMapInstanceID,
    setSourceInstanceID,
  } = moveMoneyContext;

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const loadBeneficiaries = async () => {
      try {
        setLoadingBeneficiaries(true);
        setError(null);

        const currentUserId = localStorage.getItem('currentUserId') || 'nova.brooks';

        const { beneficiaries: fetchedBeneficiaries, step2Status } =
        await initializeMoneyMovementJourney(currentUserId);

        // Store data in context
        setAccounts(step2Status.accountInfoList || []);
        setEventMapInstanceID(step2Status.eventMapInstanceID);
        setSourceInstanceID(step2Status.sourceInstanceID);
        setBeneficiaries(fetchedBeneficiaries);
        setBeneficiary(''); // reset selection on load

      } catch (err) {
        console.error('Failed to load beneficiaries:', err);
        setError('Failed to load beneficiaries. Please try again later.');
      } finally {
        setLoadingBeneficiaries(false);
      }
    };

    loadBeneficiaries();
  }, []);

  const handleNext = async () => {
    if (!beneficiary) {
      alert('Please select a beneficiary.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await validateBeneficiary(beneficiary);
      if (response.success) {
        navigate('/move-money/amount');
      } else {
        alert('Invalid beneficiary selected.');
      }
    } catch (err) {
      alert('Validation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getBeneficiaryDisplayName = (ben: Beneficiary) =>
    `${ben.receiverFirstName} ${ben.receiverLastName}`;

  const getBeneficiaryDetails = (ben: Beneficiary) => {
    const maskedAccount = ben.receiverAccountNumber
      ? `****${ben.receiverAccountNumber.slice(-4)}`
      : '';
    return `${ben.receiverBank || 'Bank'} • ${ben.receiverCurrency} • ${maskedAccount}`;
  };

  if (loadingBeneficiaries) {
    return (
      <div className="flow-step">
        <h2 className="step-header">Select a Beneficiary</h2>
        <div className="loading">Loading beneficiaries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flow-step">
        <h2 className="step-header">Select a Beneficiary</h2>
        <div className="error">{error}</div>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="flow-step">
      <h2 className="step-header">Select a Beneficiary</h2>
      {beneficiaries.length === 0 ? (
        <div className="no-beneficiaries">No beneficiaries found.</div>
      ) : (
        <ul className="selection-list">
          {beneficiaries.map(ben => (
            <li key={ben.receiverId} className="selection-item">
              <label>
                <input
                  type="radio"
                  name="beneficiary"
                  value={ben.receiverId}
                  checked={beneficiary === ben.receiverId}
                  onChange={(e) => setBeneficiary(e.target.value)}
                />
                <div className="beneficiary-info">
                  <div className="beneficiary-name">{getBeneficiaryDisplayName(ben)}</div>
                  <div className="beneficiary-details">{getBeneficiaryDetails(ben)}</div>
                  <div className="beneficiary-email">{ben.receiverEmail}</div>
                </div>
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="navigation-buttons">
        <span />
        <button
          onClick={handleNext}
          disabled={isLoading || beneficiaries.length === 0}
        >
          {isLoading ? 'Validating...' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Beneficiaries;
